'use memo';
import { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SetupNotificationsLayout, HubEventHandler } from 'layout/SetupNotificationsLayout';
import { HUB_NAMES } from 'constants/signalrConstants';
import {
  NotificationType,
  CampaignUpdatedEvent,
  RexListingCreatedEvent,
  ContractStageUpdatedEvent,
  TaskUpdatedNotification,
  PdfProcessedEvent,
  TaskCreatedNotification,
  TaskDeletedNotification,
  TaskCollectionChangedNotification,
  ContractDocumentUpdatedNotification,
} from 'types/pushNotifications';
import { emitTaskUpdate } from '../hooks/useAutopilotTaskUpdates';
import { handleCampaignUpdated, handleRexListingCreated } from './appNotificationsHandlers';
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign';
import { emitPdfProcessedEvent } from 'hooks/usePdfProcessedEvent';
import {
  useInvalidateUseGetArchivedCampaignsByAdmin,
  useInvalidateUseGetCampaignsForAgency,
  useInvalidateUseStaffGetContract,
} from '@urbanx/agx.campaigns.client';
import type { AutopilotTaskDto } from '@urbanx/agx.activities.client';
import { useInvalidateUseGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { applyTaskUpdateToTasks } from 'utils/tasks/taskUpdates';
import {
  applyTaskUpdateToSearchData,
  taskSearchQueryRoot,
  type TaskSearchData,
} from 'pages/allTasks/taskSearchQuery';
import { emitTaskCollectionChange } from 'hooks/useTaskCollectionChanges';
import {
  invalidateActiveStaffContractQueries,
  invalidateCampaignQueriesExceptTasks,
  invalidateContractDateQueries,
  refreshActiveTaskCollections,
  refreshContractStage,
  refreshTaskCollection,
} from './signalrCacheInvalidation';

interface SignalrCommsProps {
  children: ReactNode;
}

const handleAppNotificationsConnected = () => console.log('AppNotifications hub connected');

const handleAutopilotTasksConnected = () => console.log('AutopilotTasks hub connected');

const isAutopilotTasksQueryForCampaign = (queryKey: readonly unknown[], campaignId: string) =>
  queryKey[0] === 'getAutopilotTasksForCampaign' && queryKey[1] === campaignId;

const SignalrComms = ({ children }: SignalrCommsProps) => {
  const queryClient = useQueryClient();
  const invalidateCampaignListing = useInvalidateUseGetListingByCampaignId();
  const invalidateCampaigns = useInvalidateUseGetCampaignsForAgency();
  const invalidateArchivedCampaigns = useInvalidateUseGetArchivedCampaignsByAdmin();
  const invalidateSelectedContract = useInvalidateUseStaffGetContract();
  const invalidateSelectedCampaign = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      queryKey: createCampaignDetailsQueryKey(campaignId),
      exact: true,
    });
  };
  const invalidateCampaignScopedQueries = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      predicate: (query) => Array.isArray(query.queryKey) && query.queryKey.includes(campaignId),
    });
  };
  const handleTaskUpdated = (event: TaskUpdatedNotification) => {
    emitTaskUpdate(event);

    const campaignId = event.task.campaignId;
    if (!campaignId) return;

    queryClient.setQueriesData<AutopilotTaskDto[]>(
      {
        predicate: (query) => isAutopilotTasksQueryForCampaign(query.queryKey, campaignId),
      },
      (previousTasks) =>
        previousTasks ? applyTaskUpdateToTasks(previousTasks, event) : previousTasks
    );
    void invalidateContractDateQueries(queryClient, event);

    let requiresTaskListRefresh = false;
    const searchQueries = queryClient.getQueryCache().findAll({
      queryKey: taskSearchQueryRoot,
    });

    searchQueries.forEach((query) => {
      const previousData = query.state.data as TaskSearchData | undefined;
      if (!previousData) return;

      const result = applyTaskUpdateToSearchData(previousData, event);
      queryClient.setQueryData(query.queryKey, result.data);

      if (result.requiresRefresh) requiresTaskListRefresh = true;
    });

    if (requiresTaskListRefresh) {
      emitTaskCollectionChange({
        key: `membership:${event.task.id}`,
        reason: 'membership',
        taskId: event.task.id,
      });
    }
  };
  const handleTaskCreated = async (event: TaskCreatedNotification) => {
    const taskIdentity =
      event.taskId ?? [event.campaignId, event.contractId, event.taskType].join(':');

    emitTaskCollectionChange({
      key: `created:${taskIdentity}`,
      reason: 'created',
      taskId: event.taskId,
    });

    const campaignId = event.campaignId;
    if (!campaignId) return;

    await queryClient.invalidateQueries({
      predicate: (query) => isAutopilotTasksQueryForCampaign(query.queryKey, campaignId),
    });
  };
  const handleTaskDeleted = async (event: TaskDeletedNotification) => {
    emitTaskCollectionChange({
      key: `deleted:${event.taskId}`,
      reason: 'deleted',
      taskId: event.taskId,
    });

    await queryClient.invalidateQueries({
      predicate: (query) => isAutopilotTasksQueryForCampaign(query.queryKey, event.campaignId),
    });
  };
  const handleAppNotificationsReconnected = () => invalidateActiveStaffContractQueries(queryClient);
  const handleAutopilotTasksReconnected = async () => {
    emitTaskCollectionChange({
      key: 'autopilot-tasks-reconnected',
      reason: 'reconnected',
    });
    await refreshActiveTaskCollections(queryClient);
  };

  const appNotificationHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.CampaignUpdatedEvent,
      handler: (event: CampaignUpdatedEvent) =>
        handleCampaignUpdated(event, invalidateCampaignScopedQueries, invalidateSelectedCampaign, [
          invalidateCampaigns,
          invalidateArchivedCampaigns,
        ]),
    },
    {
      eventType: NotificationType.PdfProcessedEvent,
      handler: (event: PdfProcessedEvent) => emitPdfProcessedEvent(event),
    },
    {
      eventType: NotificationType.RexListingCreatedEvent,
      handler: (event: RexListingCreatedEvent) =>
        handleRexListingCreated(event, invalidateCampaignListing),
    },
    {
      eventType: NotificationType.ContractStageUpdatedEvent,
      handler: (event: ContractStageUpdatedEvent) =>
        refreshContractStage(event, invalidateSelectedContract, (campaignId) =>
          invalidateCampaignQueriesExceptTasks(queryClient, campaignId)
        ),
    },
  ];

  const autopilotTaskHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.TaskUpdatedNotification,
      handler: (event: TaskUpdatedNotification) => handleTaskUpdated(event),
    },
    {
      eventType: NotificationType.TaskCreatedNotification,
      handler: (event: TaskCreatedNotification) => handleTaskCreated(event),
    },
    {
      eventType: NotificationType.TaskDeletedNotification,
      handler: (event: TaskDeletedNotification) => handleTaskDeleted(event),
    },
    {
      eventType: NotificationType.TaskCollectionChangedNotification,
      handler: (event: TaskCollectionChangedNotification) =>
        refreshTaskCollection(queryClient, event),
    },
    {
      eventType: NotificationType.ContractDocumentUpdatedNotification,
      handler: async (event: ContractDocumentUpdatedNotification) => {
        if (!event.campaignId) return;

        await Promise.all([
          invalidateCampaignScopedQueries(event.campaignId),
          invalidateSelectedCampaign(event.campaignId),
        ]);
      },
    },
  ];
  return (
    <SetupNotificationsLayout
      hubName={HUB_NAMES.APP_NOTIFICATIONS}
      eventHandlers={appNotificationHandlers}
      onConnected={handleAppNotificationsConnected}
      onReconnected={handleAppNotificationsReconnected}
    >
      <SetupNotificationsLayout
        hubName={HUB_NAMES.AUTOPILOT_TASKS}
        eventHandlers={autopilotTaskHandlers}
        onConnected={handleAutopilotTasksConnected}
        onReconnected={handleAutopilotTasksReconnected}
      >
        {children}
      </SetupNotificationsLayout>
    </SetupNotificationsLayout>
  );
};

export default SignalrComms;
